import { RefObject, useEffect, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';

import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { colors } from 'styles/colors';

import { formatCompact, formatTooltipValue } from './helpers';
import { StatesBarTooltip } from './parts/StatesBarTooltip';
import { StateChartDatum, StatesBarUnit } from './types';

echarts.use([
  BarChart,
  GridComponent,
  TooltipComponent,
  CanvasRenderer,
]);

type RenderState = {
  data: StateChartDatum[];
  unit: StatesBarUnit;
};

interface UseGenerateStatesBarParams {
  containerRef: RefObject<HTMLDivElement>;
}

interface UseGenerateStatesBarReturn {
  renderChart: (state: RenderState) => void;
}

interface AxisTooltipParam {
  dataIndex: number;
  value: number;
}

function buildTooltipFormatter(state: RenderState): (params: AxisTooltipParam[]) => string {
  return (params) => {
    const p = params[0];
    if (!p) return '';
    const datum = state.data[p.dataIndex];
    const title = datum?.fullName || datum?.name || '';
    return ReactDOMServer.renderToString(
      <StatesBarTooltip
        name={title}
        formattedValue={formatTooltipValue(p.value, state.unit)}
      />,
    );
  };
}

function buildOption(state: RenderState): echarts.EChartsCoreOption {
  return {
    grid: {
      left: 48,
      right: 16,
      top: 32,
      bottom: 32,
      containLabel: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      borderWidth: 0,
      borderColor: 'transparent',
      formatter: buildTooltipFormatter(state),
    },
    xAxis: {
      type: 'category',
      data: state.data.map((d) => d.name),
      axisTick: { show: true, alignWithLabel: true, length: 6 },
      axisLine: { lineStyle: { color: colors.stroke['soft-200'] } },
      axisLabel: {
        fontSize: 12,
        fontWeight: 500,
        color: colors.text['main-900'],
        margin: 12,
      },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: colors.stroke['soft-200'], type: 'dashed' } },
      axisLabel: {
        fontSize: 12,
        fontWeight: 500,
        color: colors.text['sub-500'],
        formatter: (value: number) => formatCompact(value),
      },
    },
    series: [
      {
        type: 'bar',
        data: state.data.map((d) => d.value),
        barWidth: 28,
        itemStyle: {
          color: colors.brand.primary.base,
          borderRadius: [4, 4, 0, 0],
        },
        label: {
          show: true,
          position: 'top',
          fontSize: 14,
          color: colors.text['main-900'],
          formatter: (p: { value: number }) => formatCompact(p.value),
        },
      },
    ],
  };
}

export function useGenerateStatesBar({
  containerRef,
}: UseGenerateStatesBarParams): UseGenerateStatesBarReturn {
  const chartRef = useRef<echarts.ECharts | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  function ensureChart(node: HTMLDivElement): echarts.ECharts {
    if (chartRef.current) {
      if (chartRef.current.getDom() === node) return chartRef.current;
      observerRef.current?.disconnect();
      observerRef.current = null;
      chartRef.current.dispose();
      chartRef.current = null;
    }
    const chart = echarts.init(node);
    chartRef.current = chart;
    if (typeof ResizeObserver !== 'undefined') {
      let pending = 0;
      observerRef.current = new ResizeObserver(() => {
        if (pending) return;
        pending = window.requestAnimationFrame(() => {
          pending = 0;
          chartRef.current?.resize();
        });
      });
      observerRef.current.observe(node);
    }
    return chart;
  }

  function renderChart(state: RenderState): void {
    const node = containerRef.current;
    if (!node) return;
    const chart = ensureChart(node);
    chart.setOption(buildOption(state), true);
  }

  useEffect(() => () => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    chartRef.current?.dispose();
    chartRef.current = null;
  }, []);

  return { renderChart };
}
